"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, Activity, CircleAlert as AlertCircle, ArrowRight, ShieldAlert, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: "delayed-feedback",
  title: "Delayed Release Feedback",
  impact: "6 to 48 hour turnaround",
  description: "Grab-samples sent to offline QC laboratories create long hold intervals, stalling batch release workflows and tying up cleanroom capacity.",
  icon: Clock
}, {
  id: "process-drift",
  title: "Unseen Process Drift",
  impact: "Unmonitored interval gaps",
  description: "Discrete sample points fail to capture transient kinetic shifts, leading to undetected batch deviations between testing intervals.",
  icon: Activity
}, {
  id: "production-downtime",
  title: "Production Downtime",
  impact: "Costly line hold times",
  description: "Operators remain unable to adjust critical parameters in real time, forcing production halts while awaiting analytical clearance.",
  icon: AlertCircle
}];
const keyRisks = ["High-value batch quarantine risks", "Extensive manual sampling compliance overhead", "Delayed root-cause investigations"];
export default function Bottlenecks() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    whileInView: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  };
  return <section id="testingbottlenecks" data-nav="light" className="bg-secondary/40 py-16 md:py-24 border-y border-border">
      <div className="page-container">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          {/* Left Column (7 cols) - Narrative */}
          <motion.div className="lg:col-span-7 flex flex-col gap-6" {...animationProps}>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-background text-primary border-primary/20 px-3 py-1 font-medium">
                <ShieldAlert className="size-3.5 mr-1 text-primary" />
                Current Industry Challenge
              </Badge>
            </div>

            <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl leading-tight">
              The Bottleneck of Manual &amp; Offline Testing in Regulated Manufacturing
            </h2>

            <div className="space-y-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              <p>
                In regulated pharmaceutical and bioprocessing environments, traditional grab-sampling
                creates severe friction. Moving samples from the cleanroom floor to isolated QC
                laboratories leads to substantial latency before actionable data reaches operations.
              </p>
              <p>
                This retrospective approach turns quality control into an operational stopgap. Without
                in-line continuous data, teams manage critical process parameters blindly, risking
                batch variability, prolonged cycle times, and compliance burdens across the UK and Ireland.
              </p>
            </div>

            <Separator className="my-2 bg-border" />

            <div className="space-y-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                Critical Manufacturing Vulnerabilities:
              </span>
              <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {keyRisks.map((risk, i) => <li key={risk} data-index={i} className="flex items-start gap-2 text-sm text-foreground/90 bg-background/80 p-3 rounded-md border border-border">
                    <CheckCircle2 className="size-4 shrink-0 text-primary mt-0.5" />
                    <span className="leading-snug">{risk}</span>
                  </li>)}
              </ul>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/SolutionsPage">
                  Explore In-Line Solutions
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border bg-background hover:bg-muted">
                <Link to="/ContactPage">
                  Consult an Analytical Specialist
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column (5 cols) - Challenge Cards */}
          <motion.div className="lg:col-span-5" initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          x: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }}>
            <Card className="border border-border/80 shadow-md bg-card overflow-hidden">
              <CardHeader className="bg-muted/40 border-b border-border pb-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Impact Analysis
                  </span>
                  <span className="inline-flex items-center text-xs font-medium text-primary">
                    Offline vs. In-Line PAT
                  </span>
                </div>
                <CardTitle className="text-xl font-bold text-foreground">
                  The Cost of Offline Sampling
                </CardTitle>
              </CardHeader>

              <CardContent className="p-6 space-y-4">
                {challenges.map((challenge, i) => {
                const IconComponent = challenge.icon;
                return <div key={challenge.id} data-index={i} className="group rounded-lg border border-border bg-background p-4 transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-xs">
                      <div className="flex items-start gap-3.5">
                        <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                          <IconComponent className="size-5" />
                        </div>
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center justify-between gap-1.5">
                            <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors duration-200">
                              {challenge.title}
                            </h3>
                            <span className="text-xs font-medium text-muted-foreground bg-secondary px-2 py-0.5 rounded-sm">
                              {challenge.impact}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {challenge.description}
                          </p>
                        </div>
                      </div>
                    </div>;
              })}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}